// Audit logging utility
// Tracks all user actions for compliance and debugging

import { addDoc, collection, Timestamp } from 'firebase/firestore';
import { isLocalImpersonationActive } from '../dev/localImpersonation';
import { getDB } from '../firebase/config';
import type { AuditLog } from '../models';

export type AuditAction = 
  | 'create' 
  | 'update' 
  | 'delete' 
  | 'login' 
  | 'logout' 
  | 'invite_user' 
  | 'remove_user' 
  | 'accept_invitation' 
  | 'signup'
  | 'change_role'
  | 'email_verification_sent';

export type AuditResourceType = 
  | 'car' 
  | 'customer' 
  | 'rental' 
  | 'service' 
  | 'user' 
  | 'organization' 
  | 'invitation' 
  | 'auth';

interface LogAuditParams {
  userId: string;
  organizationId: string;
  action: AuditAction | 'email_verification_sent';
  resourceType: AuditResourceType;
  resourceId?: string;
  details?: Record<string, unknown>;
  ipAddress?: string;
}

/**
 * Logs a user action to the audit collection
 * This should be called for all significant user actions
 */
export async function logAudit({
  userId,
  organizationId,
  action,
  resourceType,
  resourceId,
  details,
  ipAddress,
}: LogAuditParams): Promise<void> {
  try {
    if (isLocalImpersonationActive()) {
      return;
    }

    const db = getDB();
    const auditLog: Omit<AuditLog, 'id'> = {
      userId,
      organizationId,
      action: String(action) as AuditLog['action'],
      resourceType,
      resourceId,
      timestamp: Timestamp.now(),
      details: details || {},
      ...(ipAddress && { ipAddress }),
    };

    await addDoc(collection(db, 'auditLogs'), auditLog);
  } catch (error) {
    // Don't throw - audit logging should never break the app
    console.error('Failed to log audit:', error);
  }
}

/**
 * Helper to log create actions
 */
export async function logCreate(
  userId: string,
  organizationId: string,
  resourceType: AuditResourceType,
  resourceId: string,
  details?: Record<string, unknown>
): Promise<void> {
  return logAudit({
    userId,
    organizationId,
    action: 'create',
    resourceType,
    resourceId,
    details,
  });
}

/**
 * Helper to log update actions
 */
export async function logUpdate(
  userId: string,
  organizationId: string,
  resourceType: AuditResourceType,
  resourceId: string,
  details?: Record<string, unknown>
): Promise<void> {
  return logAudit({
    userId,
    organizationId,
    action: 'update',
    resourceType,
    resourceId,
    details,
  });
}

/**
 * Helper to log delete actions
 */
export async function logDelete(
  userId: string,
  organizationId: string,
  resourceType: AuditResourceType,
  resourceId: string,
  details?: Record<string, unknown>
): Promise<void> {
  return logAudit({
    userId,
    organizationId,
    action: 'delete',
    resourceType,
    resourceId,
    details,
  });
}

/**
 * Helper to log authentication actions
 */
export async function logAuth(
  userId: string,
  organizationId: string,
  action: 'login' | 'logout' | 'signup' | 'email_verification_sent',
  details?: Record<string, unknown>
): Promise<void> {
  return logAudit({
    userId,
    organizationId,
    action,
    resourceType: 'auth',
    details,
  });
}

/**
 * Helper to log user management actions
 */
export async function logUserManagement(
  userId: string,
  organizationId: string,
  action: 'invite_user' | 'remove_user' | 'accept_invitation' | 'change_role',
  targetUserId: string,
  details?: Record<string, unknown>
): Promise<void> {
  return logAudit({
    userId,
    organizationId,
    action,
    resourceType: 'user',
    resourceId: targetUserId,
    details,
  });
}

/**
 * Helper to log organization update actions
 */
export async function logOrganizationUpdate(
  userId: string,
  organizationId: string,
  changes: Record<string, unknown>,
  details?: Record<string, unknown>
): Promise<void> {
  return logAudit({
    userId,
    organizationId,
    action: 'update',
    resourceType: 'organization',
    resourceId: organizationId,
    details: { changes, ...details },
  });
}

